import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Space, message } from "antd";
import { PlusOutlined, DownloadOutlined } from "@ant-design/icons";
import ModuleHeader from "../../components/ModuleHeader";
import DataTable from "../../components/common/DataTable";
import FilterBar from "../../components/common/FilterBar";
import StatusBadge from "../../components/common/StatusBadge";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import PageLoader from "../../components/common/PageLoader";
import usePermission from "../../hooks/usePermission";
import { getSectionConfig } from "./sectionConfigs";
import SettingsItemForm from "./SettingsItemForm";
import {
  listSettings,
  createSetting,
  updateSetting,
  deleteSetting,
  exportSettings,
} from "../../services/settings.service";

export default function SettingSectionPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermission("settings");

  const config = getSectionConfig(section);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listSettings(section, {
        page,
        per_page: pageSize,
        search,
      });
      setData(result.records || []);
      setTotalCount(result.pagination?.total_count || 0);
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [section, page, pageSize, search]);

  useEffect(() => {
    if (config) fetchData();
  }, [fetchData, config]);

  // Reset page when section changes
  useEffect(() => {
    setPage(1);
    setSearch("");
  }, [section]);

  if (!config) {
    return <div style={{ padding: 24 }}>Section not found</div>;
  }

  // Add StatusBadge rendering to status columns
  const columns = config.columns.map((col) => {
    if (col.dataIndex === "status") {
      return { ...col, render: (status) => <StatusBadge status={status} /> };
    }
    return col;
  });

  // Handlers
  const handleAdd = () => {
    setEditingRecord(null);
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setModalOpen(true);
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: `Delete ${config.label}`,
      content: `Are you sure you want to delete "${record.name}"?`,
      onConfirm: async () => {
        try {
          await deleteSetting(section, record._id);
          message.success("Deleted successfully");
          fetchData();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Delete failed");
        }
      },
    });
  };

  const handleFormSubmit = async (values) => {
    setSaving(true);
    try {
      if (editingRecord) {
        await updateSetting(section, editingRecord._id, values);
        message.success("Updated successfully");
      } else {
        await createSetting(section, values);
        message.success("Created successfully");
      }
      setModalOpen(false);
      setEditingRecord(null);
      fetchData();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportSettings(section);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      const date = new Date().toISOString().split("T")[0];
      link.href = url;
      link.setAttribute("download", `${config.label.replace(/\s+/g, "_")}_${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error("Export failed");
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handlePageChange = (newPage, newPageSize) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  if (loading && data.length === 0) {
    return <PageLoader tip={`Loading ${config.label}...`} />;
  }

  return (
    <div>
      <ModuleHeader
        title="Settings"
        subtitle={config.label}
        backTo="/settings"
        onTitleClick={() => navigate("/settings")}
      />

      <div style={{ padding: "0 24px" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 16 }}>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Export
            </Button>
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Add {config.label}
              </Button>
            )}
          </Space>
        </div>

        <FilterBar
          searchPlaceholder={`Search ${config.label.toLowerCase()}...`}
          onSearch={handleSearch}
        />

        <DataTable
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total: totalCount,
          }}
          onPageChange={handlePageChange}
          actions={{
            onEdit: canEdit ? handleEdit : null,
            onDelete: canDelete ? handleDelete : null,
            canEdit,
            canDelete,
          }}
        />
      </div>

      <SettingsItemForm
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingRecord(null);
        }}
        onSubmit={handleFormSubmit}
        fields={config.formFields}
        initialValues={editingRecord}
        title={config.label}
        loading={saving}
      />
    </div>
  );
}
